// useSavedView.js — explicit per-page "Save filters" backed by PreferencesContext.
//
// A page has a single saved default view (prefs.savedViews[pageKey]). This hook:
//   • applies the saved view (or the page default) once prefs are ready,
//   • reports `isDirty` when the live view diverges from what's saved,
//   • exposes save()/reset() for the "Save filters" / "Reset to default" buttons.
//
// Date handling: views store a date MODE (intent), never literal dates. A "custom"
// literal range is session-only — excluded from dirtiness and never saved — and is
// surfaced via `isCustomDate` so the page can show a "custom range active" banner.
import { useCallback, useEffect, useMemo, useRef } from "react";
import { usePreferences } from "../Context/PreferencesContext";

// Stable stringify (sorted keys) for shallow view objects → deep compare.
function stable(obj) {
   if (obj == null) return "null";
   if (Array.isArray(obj)) return `[${obj.map(stable).join(",")}]`;
   if (typeof obj === "object") {
      return `{${Object.keys(obj)
         .sort()
         .map((k) => `${JSON.stringify(k)}:${stable(obj[k])}`)
         .join(",")}}`;
   }
   return JSON.stringify(obj);
}

/**
 * @param {string} pageKey            e.g. "invoices"
 * @param {object} opts
 * @param {() => object} opts.getCurrent  live view object (durable fields + dateMode)
 * @param {(view:object) => void} opts.applyView  set page state from a view
 * @param {object} opts.defaultView   the page's built-in default view
 */
export function useSavedView(pageKey, { getCurrent, applyView, defaultView }) {
   const { prefsReady, getView, saveView, clearView } = usePreferences();

   const saved = getView(pageKey); // may be null
   const baseline = saved || defaultView;

   // Apply the saved view (or default) exactly once, after prefs have loaded.
   const appliedRef = useRef(false);
   // Keep latest applyView/baseline without retriggering the apply effect.
   const applyRef = useRef(applyView);
   const baselineRef = useRef(baseline);
   useEffect(() => {
      applyRef.current = applyView;
      baselineRef.current = baseline;
   });
   useEffect(() => {
      if (!prefsReady || appliedRef.current) return;
      appliedRef.current = true;
      applyRef.current(baselineRef.current);
   }, [prefsReady]);

   const current = getCurrent();
   const usesDateMode = current && "dateMode" in current;
   const isCustomDate = usesDateMode && current.dateMode === "custom";

   // What we'd actually save. Only pages that use a dateMode get the custom-date
   // fallback (a literal custom range is never persisted); pages without a dateMode
   // (e.g. InvoiceStatements) are compared/saved as-is.
   const toSavable = (view) =>
      usesDateMode ? { ...view, dateMode: view.dateMode === "custom" ? baseline.dateMode : view.dateMode } : view;
   const savable = useMemo(() => toSavable(current), [current, isCustomDate, baseline.dateMode]); // eslint-disable-line react-hooks/exhaustive-deps

   const isDirty = prefsReady && appliedRef.current && stable(savable) !== stable(baseline);

   const save = useCallback(() => {
      saveView(pageKey, toSavable(getCurrent()));
   }, [saveView, pageKey, getCurrent, baseline.dateMode]); // eslint-disable-line react-hooks/exhaustive-deps

   const reset = useCallback(() => {
      clearView(pageKey);
      applyRef.current(defaultView);
   }, [clearView, pageKey, defaultView]);

   return { isDirty, isCustomDate, hasSavedView: !!saved, baselineDateMode: baseline.dateMode, save, reset };
}
